import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={onChange ? `star-${n}` : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          className={onChange ? "transition-transform active:scale-90" : "cursor-default"}
        >
          <Star
            size={size}
            className={n <= value ? "fill-ink text-ink" : "text-ink/25"}
          />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", vehicle: "", rating: 0, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text || form.rating < 1) {
      toast.error("Please add your name, a rating, and a review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thanks for the review!");
      setForm({ name: "", vehicle: "", rating: 0, text: "" });
      load();
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="bg-cream py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal>
          <Eyebrow>What people say</Eyebrow>
          <h2 className="mt-4 font-display text-3xl leading-tight sm:text-5xl">Reviews</h2>
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="grid gap-4 sm:grid-cols-2">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.05}>
                <div data-testid="review-card" className="flex h-full flex-col rounded-3xl border border-ink/15 p-6">
                  <Quote size={22} className="text-ink/30" />
                  <p className="mt-3 flex-1 text-ink/80">“{r.text}”</p>
                  <div className="mt-4">
                    <Stars value={r.rating} />
                    <p className="mt-2 font-display text-base">{r.name}</p>
                    {r.vehicle && <p className="text-xs uppercase tracking-widest text-ink/50">{r.vehicle}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <form
              data-testid="review-form"
              onSubmit={submit}
              className="rounded-3xl bg-ink p-7 text-cream"
            >
              <h3 className="font-display text-2xl">Leave a review</h3>
              <p className="mt-2 text-sm text-cream/60">Detailed with us? Let others know.</p>

              <label className="mt-6 block text-xs font-bold uppercase tracking-widest text-cream/60">Your rating</label>
              <div className="mt-2 [&_svg]:text-cream/25 [&_.fill-ink]:fill-cream [&_.fill-ink]:text-cream">
                <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={26} />
              </div>

              <input
                data-testid="review-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="mt-5 w-full border-b-2 border-cream/25 bg-transparent py-2 text-cream placeholder:text-cream/40 focus:border-cream focus:outline-none"
              />
              <input
                data-testid="review-vehicle"
                value={form.vehicle}
                onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
                placeholder="Your vehicle (optional)"
                className="mt-4 w-full border-b-2 border-cream/25 bg-transparent py-2 text-cream placeholder:text-cream/40 focus:border-cream focus:outline-none"
              />
              <textarea
                data-testid="review-text"
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="How was your detail?"
                rows={3}
                className="mt-4 w-full resize-none border-b-2 border-cream/25 bg-transparent py-2 text-cream placeholder:text-cream/40 focus:border-cream focus:outline-none"
              />
              <button
                data-testid="review-submit"
                disabled={submitting}
                className="mt-6 w-full rounded-full bg-cream py-3.5 text-sm font-bold uppercase tracking-widest text-ink transition-transform active:scale-95 disabled:opacity-60"
              >
                {submitting ? "Posting…" : "Post Review"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
